import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useChangeBroadcastStatusQuery } from "entities/broadcasts/queries/useChangeBroadcastStatusQuery";
import { Broadcast, Status } from "entities/broadcasts/types";
import { invalidateBroadcastsData } from "modules/broadcasts/hooks/useBroadcastsData";
import { FC } from "react";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import TableWithPagination from "shared/components/TableWithPagination";
import BroadcastDetails from "./components/BroadcastDetails";
import PageSearchBar from "./components/PageSearchBar";
import { BroadcastsPageProvider, useBroadcastsPage } from "./context";
import { getUserColumns } from "./helpers";

const BroadcastsContainer = () => (
  <BroadcastsPageProvider>
    <BroadcastsPage />
  </BroadcastsPageProvider>
);

const BroadcastsPage: FC = () => {
  const { broadcastDetailsDrawer, searchData, tableData, loading } =
    useBroadcastsPage();
  const writeAccess = useRolePermission("broadcasts:write");
  const { mutateAsync: onChangeStatus } = useChangeBroadcastStatusQuery();

  const handlePublish = async (broadcastId: string, status: Status) => {
    try {
      await onChangeStatus(
        {
          broadcastId,
          status,
        },
        {
          onSuccess: async () => {
            await invalidateBroadcastsData(
              searchData.value,
              tableData.paginationModel,
            );
          },
        },
      );
    } catch (error) {
      console.error("Failed to publish broadcast:", error);
    }
  };

  const columns = getUserColumns(broadcastDetailsDrawer, handlePublish);

  return (
    <>
      <PageHeading>
        {writeAccess.allowed && (
          <PrimaryButton
            title="Create New"
            size="small"
            onClick={() => broadcastDetailsDrawer.openDrawer()}
          />
        )}
      </PageHeading>
      <PageSearchBar />
      <TableWithPagination<Broadcast>
        columns={columns}
        emptyStateMessage={
          !tableData.data.length && searchData.value
            ? "No Results Found"
            : "You haven't created any broadcasts yet. Use the 'Create New' button to create one."
        }
        paginationMode="server"
        pageSizeOptions={[25, 50, 100]}
        loading={loading}
        onRowClick={(params) =>
          broadcastDetailsDrawer.openDrawer(params.row.id)
        }
        {...tableData}
      />
      <BroadcastDetails />
    </>
  );
};

export default BroadcastsContainer;
